import { EnvironmentType } from "@app/models/env";
import { environment } from "../../environments/environment";
import salesforceNativeCredentials from "./salesforce-native.credentials.json";
import salesforceNativeProfile from "./salesforce-native.profile.json";

/**
 * SF-NATIVE — Salesforce Marketing Cloud Mobile Push SDK (iOS/Android Cordova).
 * Credenciais device (appEndpoint) NÃO servem para REST web; ver SF-WEB-* acima.
 *
 * Fonte única (nativo): salesforce-native.credentials.json
 * DEV / HML / HEXT → development | master/production → production
 */

interface SalesforceConfig {
  accessToken: string;
  appId: string;
  appEndpoint: string;
  label?: string;
  /**
   * MID (Member ID) - Identificador do Business Unit no Salesforce
   * Necessário para algumas operações do SDK
   */
  mid?: string;
  /**
   * Contact Key Prefix - Prefixo para o identificador único do cliente
   * Usaremos o PV (código interno) como contact_key
   */
  contactKeyPrefix?: string;
}

const SALESFORCE_DEV_CONFIG: SalesforceConfig = salesforceNativeCredentials.development;
const SALESFORCE_PROD_CONFIG: SalesforceConfig = salesforceNativeCredentials.production;

/**
 * Retorna a configuração do Salesforce baseada no ambiente atual.
 * Regra: environment production → credenciais production (obrigatório).
 */
export const getSalesforceConfig = (): SalesforceConfig => {
  const isProd = environment === EnvironmentType.production;
  const config = isProd ? SALESFORCE_PROD_CONFIG : SALESFORCE_DEV_CONFIG;

  if (isProd && config.appId === SALESFORCE_DEV_CONFIG.appId) {
    console.error(
      "[Salesforce] ERRO: app em production mas credenciais SFMC são DEV. " +
        "Rode o build com ENVIRONMENT=production antes do cap sync."
    );
  }

  if (isProd && salesforceNativeProfile.profile !== "production") {
    console.error(
      "[Salesforce] ERRO: perfil nativo pinado não é production. " +
        `Atual: ${salesforceNativeProfile.profile} (fonte: ${salesforceNativeProfile.source}). ` +
        "Execute: node scripts/pin-salesforce-profile.js production && ENVIRONMENT=production npx cap sync"
    );
  }

  return config;
};

/** Label legível do app MobilePush SFMC (Dev vs Production). */
export const getSalesforceNativeProfileLabel = (): string => {
  const isProd = environment === EnvironmentType.production;
  return (isProd ? SALESFORCE_PROD_CONFIG : SALESFORCE_DEV_CONFIG).label ?? (isProd ? "production" : "development");
};

// =============================================================================
// WEB — Salesforce Marketing Cloud no browser (sem Cordova)
// =============================================================================
// Guia completo: docs/SALESFORCE_INTEGRATION.md
// Contrato API produção: docs/SALESFORCE_WEB.md
//
// SF-WEB-PRODUCTION  → transport "backend" → Supermup → SFMC (secrets no servidor)
// SF-WEB-PROVISIONAL → transport "direct"  → SFMC REST no browser (só teste local)
//
// Produção web: getSalesforceWebTransport() FORÇA "backend" se environment === production.
// Para desligar teste local: SALESFORCE_WEB_CONFIG.transport = "backend"
// =============================================================================

/** backend = produção (Supermup). direct = teste REST local (provisório). log-only = só console (QA de mapeamento). */
export type SalesforceWebTransport = "backend" | "direct" | "log-only";

/** Extrai subdomínio MC do endpoint device (ex.: mcxxx.device.marketingcloudapis.com). */
const extractMcSubdomain = (appEndpoint: string): string => {
  try {
    const host = new URL(appEndpoint).hostname;
    return host.replace(".device.marketingcloudapis.com", "");
  } catch {
    return "mcqbhlpk8b30t4-st1vjq1827w54";
  }
};

const mcSubdomain = extractMcSubdomain(
  (environment === EnvironmentType.production
    ? SALESFORCE_PROD_CONFIG
    : SALESFORCE_DEV_CONFIG
  ).appEndpoint
);

/**
 * SF-WEB-PROVISIONAL — credenciais e opções do modo `direct` (teste local).
 * NÃO usar em produção. Ver docs/SALESFORCE_INTEGRATION.md.
 */
export const SALESFORCE_WEB_DIRECT_CONFIG = {
  /** Desligado automaticamente em production */
  enabled: environment !== EnvironmentType.production,
  /** Requer `ionic serve` + proxy.conf.json (ver angular.json → serve.options) */
  useDevProxy: true,
  subdomain: mcSubdomain,
  /**
   * API Integration REST (recomendado). Se vazio, tenta appId + accessToken do Mobile Push.
   */
  restClientId: "",
  restClientSecret: "",
  fallbackToMobileSdkCredentials: true,
  /** Mapeamento event_name → EventDefinitionKey no Journey Builder */
  eventDefinitionKeys: {} as Record<string, string>,
};

/**
 * SF-WEB — config compartilhada (produção + provisório).
 * `transport` escolhe o caminho; `eventsPath`/`contactSyncPath` só valem em modo backend.
 */
export const SALESFORCE_WEB_CONFIG = {
  enabled: true,
  /**
   * SF-WEB-PRODUCTION: "backend" — POST para API Supermup (padrão em production).
   * SF-WEB-PROVISIONAL: "direct"  — REST SFMC no browser (default em dev/hml/hext até migrar).
   */
  /**
   * Dev/hml/hext: "log-only" — valida mapeamento no console sem bater no SFMC.
   * Troque para "direct" só se tiver REST + proxy; para "backend" quando a API Supermup existir.
   */
  transport: (environment !== EnvironmentType.production
    ? "log-only"
    : "backend") as SalesforceWebTransport,
  /** SF-WEB-PRODUCTION — contrato em docs/SALESFORCE_WEB.md */
  eventsPath: "engine/marketing/salesforce/event",
  /** SF-WEB-PRODUCTION — opcional; sync de contato após login */
  contactSyncPath: "engine/marketing/salesforce/contact",
};

/**
 * Resolve o transporte web efetivo. IA/manutenção: comece por aqui ao debugar web.
 */
export const getSalesforceWebTransport = (): SalesforceWebTransport => {
  if (environment === EnvironmentType.production) {
    return "backend";
  }
  if (!SALESFORCE_WEB_DIRECT_CONFIG.enabled && SALESFORCE_WEB_CONFIG.transport === "direct") {
    return "log-only";
  }
  return SALESFORCE_WEB_CONFIG.transport;
};

export const SALESFORCE_SDK_CONFIG = {
  /**
   * Habilitar logging do SDK (apenas em ambientes não-produção)
   */
  analyticsEnabled: true,

  /**
   * Habilitar logging do SDK (apenas em ambientes não-produção)
   */
  enableLogging: environment !== EnvironmentType.production,

  /**
   * Tempo de delay para inicialização (ms)
   * Útil para garantir que o app está pronto
   */
  delayRegistration: 1000,
};
